/**
 * MarkerAllocation.jsx — Phase 2C C1: Marker Allocation UI
 *
 * Coordinator-only workspace to:
 *   1. Select an offering + component (scope selector)
 *   2. View all evaluation_cases with their assignment status
 *   3. Assign markers (PRIMARY, SECOND, MODERATOR) with SOD enforcement
 *   4. Override individual student assignments with a reason
 *   5. View and revoke assignment history per case
 *
 * API surface consumed:
 *   GET  /api/delivery/offerings
 *   GET  /api/evaluation/cases?offering_id=...&component_id=...
 *   GET  /api/evaluation/cases/{id}/assignments
 *   GET  /api/users/{institution_id}                    (faculty filter)
 *   POST /api/evaluation/marker-assignments
 *   POST /api/evaluation/marker-assignments/{id}/deactivate
 *   PATCH /api/evaluation/marker-assignments/{id}
 *
 * SOD errors (409 INV-2C-SOD-01) are surfaced inline in the assignment dialog.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  UserPlus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
  ShieldAlert,
  History,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  UserCheck,
  BadgeAlert,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = Object.assign(
      new Error(
        body?.detail?.message || (typeof body?.detail === "string" ? body.detail : null) || r.statusText
      ),
      { status: r.status, detail: body?.detail }
    );
    throw err;
  }
  return body;
}

// ─── Role config ────────────────────────────────────────────────────────────

const COORDINATOR_ROLES = new Set([
  "super_admin", "institution_admin", "programme_manager", "registrar", "exam_admin",
]);

const MARKER_ROLE_OPTIONS = [
  { value: "PRIMARY_MARKER",  label: "Primary Marker",  desc: "First/only marking round" },
  { value: "SECOND_MARKER",   label: "Second Marker",   desc: "Independent blind second mark" },
  { value: "MODERATOR",       label: "Moderator",       desc: "Resolves primary/second disagreement" },
];

const ASSIGNMENT_TYPES = ["SECTION", "STUDENT_OVERRIDE"];

// ─── Status badge ────────────────────────────────────────────────────────────

function CaseStatusBadge({ status }) {
  const MAP = {
    UNASSIGNED:           "bg-slate-800 text-slate-400 border-slate-700",
    ASSIGNED:             "bg-indigo-950 text-indigo-300 border-indigo-800/60",
    MARKING_IN_PROGRESS:  "bg-sky-950 text-sky-300 border-sky-800/60",
    MARKED:               "bg-teal-950 text-teal-300 border-teal-800/60",
    SECOND_MARK_REQUIRED: "bg-amber-950 text-amber-300 border-amber-800/60",
    SECOND_MARKED:        "bg-lime-950 text-lime-300 border-lime-800/60",
    MODERATION_REQUIRED:  "bg-orange-950 text-orange-300 border-orange-800/60",
    MODERATED:            "bg-violet-950 text-violet-300 border-violet-800/60",
    READY_FOR_RATIFICATION: "bg-emerald-950 text-emerald-300 border-emerald-800/60",
    RATIFIED:             "bg-green-950 text-green-300 border-green-800/60",
    PUBLISHED:            "bg-cyan-950 text-cyan-300 border-cyan-800/60",
  };
  const cls = MAP[status] || "bg-slate-800 text-slate-400 border-slate-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function MarkerRoleBadge({ role }) {
  const MAP = {
    PRIMARY_MARKER:  "bg-indigo-950/50 text-indigo-300 border-indigo-800/40",
    SECOND_MARKER:   "bg-sky-950/50 text-sky-300 border-sky-800/40",
    MODERATOR:       "bg-amber-950/50 text-amber-300 border-amber-800/40",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${MAP[role] || "bg-slate-800 text-slate-400 border-slate-700"}`}>
      {role?.replace(/_/g, " ")}
    </span>
  );
}

// ─── SOD Error Banner ────────────────────────────────────────────────────────

function SODErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  const isSOD = error?.detail?.error_code === "INV-2C-SOD-01";
  return (
    <div
      data-testid="sod-error-banner"
      className={`rounded-lg border px-4 py-3 flex items-start gap-3 mb-3 ${
        isSOD
          ? "border-red-800/60 bg-red-950/30 text-red-300"
          : "border-amber-800/60 bg-amber-950/30 text-amber-300"
      }`}
    >
      <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {isSOD && (
          <div className="text-xs font-bold mb-0.5 text-red-400">
            SOD Violation — INV-2C-SOD-01
          </div>
        )}
        <div className="text-xs leading-relaxed">{error.message || String(error)}</div>
      </div>
      <button onClick={onDismiss} className="text-slate-500 hover:text-slate-300">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Assignment History Row ───────────────────────────────────────────────────

function AssignmentHistoryPanel({ caseId, facultyMap, onRevoke }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/evaluation/cases/${caseId}/assignments`);
      setAssignments(data.assignments || []);
    } catch (e) {
      toast.error("Failed to load assignments: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (assignmentId) => {
    if (!window.confirm("Revoke this assignment? This cannot be undone if the marker has already submitted.")) return;
    setRevoking(assignmentId);
    try {
      await apiFetch(`/evaluation/marker-assignments/${assignmentId}/deactivate`, { method: "POST" });
      toast.success("Assignment revoked");
      load();
      onRevoke?.();
    } catch (e) {
      toast.error("Revoke failed: " + e.message);
    } finally {
      setRevoking(null);
    }
  };

  if (loading) return (
    <div className="py-4 flex items-center gap-2 text-xs text-slate-500">
      <RefreshCw className="w-3 h-3 animate-spin" /> Loading assignments…
    </div>
  );

  if (assignments.length === 0) return (
    <div className="py-4 text-xs text-slate-500 italic">No assignments yet.</div>
  );

  return (
    <div className="mt-2 space-y-2" data-testid="assignment-history-panel">
      {assignments.map((a) => {
        const name = facultyMap[a.marker_id] || a.marker_id?.slice(0, 8) + "…";
        const isRevoked = !!a.revoked_at;
        return (
          <div
            key={a.id}
            data-testid={`assignment-row-${a.id}`}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border text-xs ${
              isRevoked
                ? "border-slate-700/40 bg-slate-900/30 opacity-60"
                : "border-slate-700/60 bg-slate-900/60"
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <MarkerRoleBadge role={a.marker_role} />
                <span className="text-slate-200 font-medium">{name}</span>
                {isRevoked && (
                  <span className="text-red-400/80 border border-red-800/40 bg-red-950/30 px-1.5 py-0 rounded text-[10px]">
                    Revoked
                  </span>
                )}
                {a.override_reason && (
                  <span className="text-slate-500 italic truncate max-w-[140px]" title={a.override_reason}>
                    "{a.override_reason}"
                  </span>
                )}
              </div>
              <div className="text-slate-500 mt-0.5 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
                {isRevoked && (
                  <span className="ml-2 text-red-400/60">
                    · Revoked {new Date(a.revoked_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            {!isRevoked && (
              <button
                data-testid={`revoke-btn-${a.id}`}
                onClick={() => handleRevoke(a.id)}
                disabled={revoking === a.id}
                className="text-slate-500 hover:text-red-400 transition-colors disabled:opacity-50"
                title="Revoke assignment"
              >
                {revoking === a.id ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Assign Marker Drawer ─────────────────────────────────────────────────────

function AssignMarkerDrawer({ open, onClose, evalCase, offeringId, componentId, faculty, onAssigned }) {
  const [form, setForm] = useState({
    marker_role: "PRIMARY_MARKER",
    marker_id: "",
    assignment_type: "SECTION",
    override_reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [sodError, setSodError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ marker_role: "PRIMARY_MARKER", marker_id: "", assignment_type: "SECTION", override_reason: "" });
      setSodError(null);
    }
  }, [open, evalCase?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.marker_id) { toast.error("Select a marker"); return; }
    setSaving(true);
    setSodError(null);
    try {
      await apiFetch("/evaluation/marker-assignments", {
        method: "POST",
        body: JSON.stringify({
          offering_id: offeringId,
          component_id: componentId,
          marker_id: form.marker_id,
          marker_role: form.marker_role,
          assignment_type: form.assignment_type,
          evaluation_case_id: evalCase?.id || null,
          override_reason: form.override_reason || null,
        }),
      });
      toast.success(`${form.marker_role.replace(/_/g, " ")} assigned`);
      onAssigned();
      onClose();
    } catch (e) {
      if (e.status === 409 && e.detail?.error_code === "INV-2C-SOD-01") {
        setSodError(e);
      } else if (e.status === 409) {
        setSodError(e);
      } else {
        toast.error(e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const isOverride = form.assignment_type === "STUDENT_OVERRIDE";

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="assign-marker-drawer">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" onClick={onClose} />
      {/* Panel */}
      <div className="w-[420px] bg-slate-900 border-l border-slate-700 flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-100">Assign Marker</div>
            {evalCase && (
              <div className="text-xs text-slate-500 mt-0.5">
                Case: {evalCase.id?.slice(0, 12)}… · Status: <CaseStatusBadge status={evalCase.status} />
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 px-6 py-5 space-y-5">
          <SODErrorBanner error={sodError} onDismiss={() => setSodError(null)} />

          {/* Assignment type */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Assignment Type</label>
            <div className="flex gap-2">
              {ASSIGNMENT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`assignment-type-${t}`}
                  onClick={() => setForm((f) => ({ ...f, assignment_type: t }))}
                  className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${
                    form.assignment_type === t
                      ? "bg-indigo-900/40 border-indigo-700 text-indigo-300"
                      : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300"
                  }`}
                >
                  {t === "SECTION" ? "Section-wide" : "Student Override"}
                </button>
              ))}
            </div>
            {isOverride && (
              <div className="mt-2 flex items-start gap-2 text-xs text-amber-400 bg-amber-950/20 border border-amber-800/30 rounded-lg px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Override targets this specific student's case. A reason is required.
              </div>
            )}
          </div>

          {/* Marker role */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Marker Role</label>
            <div className="space-y-2">
              {MARKER_ROLE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  data-testid={`role-option-${opt.value}`}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                    form.marker_role === opt.value
                      ? "border-indigo-700/60 bg-indigo-950/30"
                      : "border-slate-700/60 hover:border-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    name="marker_role"
                    value={opt.value}
                    checked={form.marker_role === opt.value}
                    onChange={() => setForm((f) => ({ ...f, marker_role: opt.value }))}
                    className="mt-0.5 accent-indigo-500"
                  />
                  <div>
                    <div className="text-xs font-medium text-slate-200">{opt.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Faculty picker */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Select Faculty</label>
            <select
              data-testid="faculty-select"
              value={form.marker_id}
              onChange={(e) => { setForm((f) => ({ ...f, marker_id: e.target.value })); setSodError(null); }}
              required
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-indigo-600"
            >
              <option value="">— Choose faculty member —</option>
              {faculty.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name || f.email} ({f.role})
                </option>
              ))}
            </select>
          </div>

          {/* Override reason */}
          {isOverride && (
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Override Reason <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="override-reason-input"
                value={form.override_reason}
                onChange={(e) => setForm((f) => ({ ...f, override_reason: e.target.value }))}
                rows={3}
                required
                placeholder="Explain why this student is being assigned to a specific marker…"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-indigo-600 resize-none placeholder:text-slate-600"
              />
            </div>
          )}

          {/* SOD note */}
          <div className="flex items-start gap-2 text-[10px] text-slate-500 bg-slate-800/50 border border-slate-700/40 rounded-lg px-3 py-2">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-600" />
            <span>
              SOD is enforced. The same person cannot hold PRIMARY, SECOND, MODERATOR, or RATIFIER roles
              on the same case. Conflicts are rejected with error code <strong className="text-slate-400">INV-2C-SOD-01</strong>.
            </span>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="assign-marker-submit"
            onClick={handleSubmit}
            disabled={saving || !form.marker_id}
            className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium transition-colors flex items-center gap-2"
          >
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Case Row ─────────────────────────────────────────────────────────────────

function CaseRow({ evalCase, facultyMap, onAssign, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  const activeAssignments = evalCase._assignments?.filter((a) => !a.revoked_at) || [];
  const rolesSummary = activeAssignments.map((a) => ({
    role: a.marker_role,
    name: facultyMap[a.marker_id] || "…",
  }));

  const isFullyStaffed = rolesSummary.some((r) => r.role === "PRIMARY_MARKER");

  return (
    <>
      <tr
        data-testid={`case-row-${evalCase.id}`}
        className="border-b border-slate-800/60 hover:bg-slate-800/20 transition-colors group"
      >
        <td className="px-4 py-3">
          <div className="text-xs font-mono text-slate-400">{evalCase.id?.slice(0, 12)}…</div>
          {evalCase.attempt_id && (
            <div className="text-[10px] text-slate-600 mt-0.5">{evalCase.attempt_id?.slice(0, 12)}…</div>
          )}
        </td>
        <td className="px-4 py-3">
          <CaseStatusBadge status={evalCase.status} />
        </td>
        <td className="px-4 py-3">
          {rolesSummary.length === 0 ? (
            <span className="text-xs text-slate-600 italic">None</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {rolesSummary.map((r, i) => (
                <span key={i} className="flex items-center gap-1 text-xs">
                  <MarkerRoleBadge role={r.role} />
                  <span className="text-slate-400">{r.name}</span>
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-slate-500">
          {evalCase.resolved_mark != null ? (
            <span className="text-emerald-400 font-medium">
              {evalCase.resolved_mark} / {evalCase.max_mark}
            </span>
          ) : "—"}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              data-testid={`assign-btn-${evalCase.id}`}
              onClick={() => onAssign(evalCase)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-700/50 bg-indigo-950/30 text-indigo-300 text-xs hover:bg-indigo-900/40 hover:border-indigo-600 transition-colors"
            >
              <UserPlus className="w-3 h-3" />
              Assign
            </button>
            <button
              data-testid={`history-btn-${evalCase.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              <History className="w-3 h-3" />
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`assignment-history-${evalCase.id}`} className="bg-slate-900/50">
          <td colSpan={5} className="px-8 pb-4 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-2">
              Assignment History
            </div>
            <AssignmentHistoryPanel
              caseId={evalCase.id}
              facultyMap={facultyMap}
              onRevoke={onRefresh}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MarkerAllocation() {
  const { user } = useAuth();

  const [scopes, setScopes] = useState([]);
  const [selectedOffering, setSelectedOffering] = useState("");
  const [selectedComponent, setSelectedComponent] = useState("");

  const [cases, setCases] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [facultyMap, setFacultyMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [initialising, setInitialising] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCase, setDrawerCase] = useState(null);

  const [bootstrapping, setBootstrapping] = useState(false);

  const institutionId = user?.institution_id;

  // Load scopes + faculty in parallel
  useEffect(() => {
    if (!institutionId) return;
    setInitialising(true);
    Promise.all([
      apiFetch("/evaluation/scopes"),
      apiFetch(`/users/${institutionId}`),
    ])
      .then(([scopeData, userList]) => {
        setScopes(scopeData?.scopes || []);
        const fac = (userList || []).filter((u) =>
          ["faculty", "instructor"].includes(u.role)
        );
        setFaculty(fac);
        const map = {};
        (userList || []).forEach((u) => { map[u.id] = u.name || u.email; });
        setFacultyMap(map);
      })
      .catch((e) => toast.error("Load failed: " + e.message))
      .finally(() => setInitialising(false));
  }, [institutionId]);

  // Auto-populate component when only one scope
  useEffect(() => {
    const scopeMatch = scopes.filter((s) => s.offering_id === selectedOffering);
    if (scopeMatch.length === 1) {
      setSelectedComponent(scopeMatch[0].component_id);
    } else {
      setSelectedComponent("");
    }
  }, [selectedOffering, scopes]);

  // Load cases when scope changes
  const loadCases = useCallback(async () => {
    if (!selectedOffering) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ offering_id: selectedOffering });
      if (selectedComponent) params.append("component_id", selectedComponent);
      const data = await apiFetch(`/evaluation/cases?${params}`);
      setCases(data.cases || []);
    } catch (e) {
      toast.error("Failed to load cases: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOffering, selectedComponent]);

  useEffect(() => { loadCases(); }, [loadCases]);

  // Bootstrap evaluation cases (create evaluation_case rows from submitted attempts)
  const handleBootstrap = async () => {
    if (!selectedOffering) { toast.error("Select an offering first"); return; }
    const scope = scopes.find((s) => s.offering_id === selectedOffering && (s.component_id === selectedComponent || !selectedComponent));
    const componentId = selectedComponent || scope?.component_id;

    if (!componentId) {
      toast.error("Cannot determine component ID. Select a specific component first.");
      return;
    }
    // Use a placeholder session ID derived from the offering ID for demo purposes
    const sessionId = selectedOffering.replace(/-/g, "").slice(0, 8) + "-sess-sess-sess-sess" + "0".repeat(4);
    setBootstrapping(true);
    try {
      const result = await apiFetch("/evaluation/cases/bootstrap", {
        method: "POST",
        body: JSON.stringify({
          offering_id: selectedOffering,
          pg_session_id: sessionId,
          component_id: componentId,
        }),
      });
      toast.success(`Bootstrapped ${result.created?.length ?? 0} evaluation case(s)`);
      loadCases();
    } catch (e) {
      toast.error("Bootstrap failed: " + e.message);
    } finally {
      setBootstrapping(false);
    }
  };

  const openDraw = (evalCase) => {
    setDrawerCase(evalCase);
    setDrawerOpen(true);
  };

  if (!COORDINATOR_ROLES.has(user?.role)) {
    return (
      <EvaluationShell title="Marker Allocation" breadcrumb="Marker Allocation">
        <div className="flex items-center gap-3 p-6 rounded-xl border border-red-800/50 bg-red-950/20 text-red-300 text-sm">
          <BadgeAlert className="w-5 h-5 flex-shrink-0" />
          Marker Allocation requires a Coordinator or Programme Manager role.
        </div>
      </EvaluationShell>
    );
  }

  return (
    <EvaluationShell title="Marker Allocation" breadcrumb="Marker Allocation">
      {/* Scope selector */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="scope-selector">
      <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Assessment Offering</label>
          <select
            data-testid="offering-select"
            value={selectedOffering}
            onChange={(e) => setSelectedOffering(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-indigo-600"
          >
            <option value="">— Select an offering —</option>
            {[...new Map(scopes.map((s) => [s.offering_id, s])).values()].map((s) => (
              <option key={s.offering_id} value={s.offering_id}>
                {`${s.offering_id?.slice(0, 16)}… — ${s.case_count} case${s.case_count !== 1 ? "s" : ""}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button
            data-testid="refresh-cases-btn"
            onClick={loadCases}
            disabled={!selectedOffering || loading}
            className="px-4 py-2 rounded-lg border border-slate-700 text-slate-400 text-sm hover:border-slate-600 hover:text-slate-200 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="bootstrap-cases-btn"
            onClick={handleBootstrap}
            disabled={!selectedOffering || bootstrapping}
            className="px-4 py-2 rounded-lg border border-indigo-700/50 bg-indigo-950/20 text-indigo-300 text-sm hover:bg-indigo-900/30 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            {bootstrapping ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Bootstrap Cases
          </button>
        </div>
      </div>

      {/* Cases table */}
      {!selectedOffering ? (
        <div
          data-testid="no-offering-prompt"
          className="flex flex-col items-center justify-center py-16 text-slate-500"
        >
          <UserCheck className="w-10 h-10 mb-3 opacity-30" />
          <div className="text-sm">Select an offering above to view evaluation cases</div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading cases…</span>
        </div>
      ) : cases.length === 0 ? (
        <div
          data-testid="no-cases-prompt"
          className="flex flex-col items-center justify-center py-16 text-slate-500"
        >
          <Info className="w-10 h-10 mb-3 opacity-30" />
          <div className="text-sm">No evaluation cases found for this offering.</div>
          <div className="text-xs mt-1">
            Use <strong className="text-indigo-400">Bootstrap Cases</strong> to create cases from submitted attempts.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="cases-table">
          <div className="bg-slate-900/60 px-4 py-3 flex items-center justify-between border-b border-slate-800">
            <span className="text-xs font-semibold text-slate-400">
              {cases.length} evaluation case{cases.length !== 1 ? "s" : ""}
            </span>
            <button
              data-testid="assign-all-btn"
              onClick={() => openDraw(null)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Assign (Section-wide)
            </button>
          </div>
          <table className="w-full text-sm" data-testid="cases-table-inner">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Case ID</th>
                <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Assignments</th>
                <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Resolved Mark</th>
                <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <CaseRow
                  key={c.id}
                  evalCase={c}
                  facultyMap={facultyMap}
                  onAssign={openDraw}
                  onRefresh={loadCases}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign marker drawer */}
      <AssignMarkerDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerCase(null); }}
        evalCase={drawerCase}
        offeringId={selectedOffering}
        componentId={selectedComponent || cases[0]?.component_id || ""}
        faculty={faculty}
        onAssigned={() => {
          loadCases();
          setDrawerOpen(false);
        }}
      />
    </EvaluationShell>
  );
}
